import { supabase } from './supabase';

function clean(value: unknown) {
  return String(value || '').replace(/^\uFEFF/, '').trim().replace(/^['"]|['"]$/g, '').replace(/\/+$/, '');
}

const configuredApiUrl = clean(import.meta.env.VITE_API_URL);
export const API_URL = configuredApiUrl;
const validProductionApi = /^https:\/\/[a-z0-9.-]+(?::\d+)?$/i.test(API_URL);
const validLocalApi = import.meta.env.DEV && /^http:\/\/(localhost|127\.0\.0\.1)(?::\d+)?$/i.test(API_URL);
export const apiConfigurationIssues: string[] = (validProductionApi || validLocalApi)
  ? []
  : ['VITE_API_URL is missing or malformed.'];

async function requestWithTimeout(url: string, options: RequestInit = {}, timeoutMs = 30000): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...options,
      signal: options.signal || controller.signal,
    });
  } finally {
    window.clearTimeout(timer);
  }
}

async function parseJsonResponse(response: Response): Promise<any> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`MAGNA Accounting API returned an unexpected non-JSON response (${response.status}).`);
  }
}

export type ApiHealth = {
  ok: boolean;
  service: string;
  version: string;
  now: string;
  configuration: { ok: boolean; issues: string[] };
};

export async function getApiHealth(): Promise<ApiHealth> {
  if (apiConfigurationIssues.length) throw new Error(apiConfigurationIssues[0]);
  let response: Response;
  try {
    response = await requestWithTimeout(`${API_URL}/health`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }, 12000);
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Network request failed';
    throw new Error(`Cannot reach ${API_URL}. ${detail}`);
  }
  const payload = await parseJsonResponse(response);
  if (!response.ok || !payload?.ok || payload?.configuration?.ok !== true) {
    const issues = Array.isArray(payload?.configuration?.issues) ? payload.configuration.issues.join('; ') : '';
    throw new Error(issues || `API health check failed (${response.status}).`);
  }
  return payload as ApiHealth;
}

export async function api<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Your session has expired. Please sign in again.');
  if (apiConfigurationIssues.length) throw new Error(apiConfigurationIssues[0]);

  let response: Response;
  try {
    response = await requestWithTimeout(`${API_URL}${path}`, {
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  } catch (error) {
    const detail = error instanceof Error
      ? (error.name === 'AbortError' ? 'Request timed out.' : error.message)
      : 'Network request failed';
    throw new Error(`Cannot reach the MAGNA Accounting v3 API at ${API_URL}. ${detail}`);
  }

  const payload = await parseJsonResponse(response);
  if (!response.ok) {
    const requestId = response.headers.get('x-request-id') || payload.request_id;
    throw new Error(`${payload.error || `Request failed (${response.status})`}${requestId ? ` (Request ID: ${requestId})` : ''}`);
  }
  return payload as T;
}

export async function uploadAccountingDocument(args: {
  sourceModule: string;
  sourceId: string;
  documentType?: string;
  file: File;
}) {
  const signed = await api<{ path: string; token: string }>('/api/documents/upload-url', {
    method: 'POST',
    body: JSON.stringify({
      source_module: args.sourceModule,
      source_id: args.sourceId,
      file_name: args.file.name,
      content_type: args.file.type,
    }),
  });

  const { error } = await supabase.storage
    .from('accounting-documents')
    .uploadToSignedUrl(signed.path, signed.token, args.file, { contentType: args.file.type, upsert: false });
  if (error) throw error;

  return api('/api/documents/complete', {
    method: 'POST',
    body: JSON.stringify({
      source_module: args.sourceModule,
      source_id: args.sourceId,
      document_type: args.documentType || 'supporting_document',
      file_name: args.file.name,
      storage_path: signed.path,
      mime_type: args.file.type,
      file_size: args.file.size,
    }),
  });
}

export async function downloadApi(path: string): Promise<{ blob: Blob; filename: string }> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Your session has expired. Please sign in again.');
  if (apiConfigurationIssues.length) throw new Error(apiConfigurationIssues[0]);

  let response: Response;
  try {
    response = await requestWithTimeout(`${API_URL}${path}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
      cache: 'no-store',
    }, 60000);
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'Network request failed';
    throw new Error(`Cannot reach the MAGNA Accounting v3 API at ${API_URL}. ${detail}`);
  }

  if (!response.ok) {
    let payload: any = {};
    try { payload = await response.json(); } catch { /* ignore */ }
    throw new Error(payload.error || `Download failed (${response.status})`);
  }

  const disposition = response.headers.get('content-disposition') || '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const quoted = /filename="([^"]+)"/i.exec(disposition)?.[1];
  const plain = /filename=([^;]+)/i.exec(disposition)?.[1]?.trim();
  const filename = encoded ? decodeURIComponent(encoded) : (quoted || plain || 'magna-accounting-backup.json');
  return { blob: await response.blob(), filename };
}

export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
